import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import "./App.css";

const API_URL = "https://saas-scenario-engine.onrender.com/analyze";

/* ── formatting ─────────────────────────────────────────────────────────────── */
const fmt = (n) => {
  if (n === null || n === undefined || !isFinite(n)) return "—";
  const a = Math.abs(n);
  const s = n < 0 ? "-" : "";
  if (a >= 1_000_000) return `${s}$${(a / 1_000_000).toFixed(2)}M`;
  if (a >= 1_000) return `${s}$${(a / 1_000).toFixed(1)}K`;
  return `${s}$${a.toFixed(0)}`;
};
const pct = (v) => `${(v * 100).toFixed(1)}%`;
const csvNum = (n) => (isFinite(n) ? n.toFixed(2) : "");

/* ── deterministic math engine (mirrors backend/engine.py exactly) ──────────── */

/* Python's round(x, 2) rounds the double's exact value, breaking .xx5 ties
   toward even. Math.round(n * 100) / 100 gets this wrong twice over: it rounds
   ties away from zero, and the multiply itself perturbs values enough to
   invent ties that aren't there (-413.59499999999997 * 100 lands on exactly
   -41359.5). Both drift the port off engine.py — see engineParity.test.js.

   A double's exact value only terminates at three decimals when it is a
   multiple of 1/8, so n * 8 — lossless, being a power of two — identifies a
   genuine tie: an odd multiple of an eighth. Everything else goes through
   toFixed, which rounds the exact value with no intermediate multiply. */
export const round2 = (n) => {
  if (!isFinite(n)) return n;
  const eighths = n * 8;
  if (Number.isInteger(eighths) && eighths % 2 !== 0) {
    const scaled = eighths * 12.5; // exactly n * 100, and always ends in .5
    const lower = Math.floor(scaled);
    return (lower % 2 === 0 ? lower : lower + 1) / 100;
  }
  return Number(n.toFixed(2));
};

export function calculateProjections({ current_mrr, churn_rate, growth_rate }, churnMult, growthMult, months = 12) {
  const adjChurn = churn_rate * churnMult;
  const adjGrowth = growth_rate * growthMult;
  const rows = [];
  let mrr = current_mrr;
  for (let m = 1; m <= months; m++) {
    const churn_loss = mrr * adjChurn;
    const new_revenue = mrr * adjGrowth;
    const net_change = new_revenue - churn_loss;
    mrr = mrr + net_change;
    rows.push({
      month: m,
      mrr: round2(mrr),
      arr: round2(mrr * 12),
      churn_loss: round2(churn_loss),
      new_revenue: round2(new_revenue),
      net_change: round2(net_change),
    });
  }
  return rows;
}

/* Mirrors engine.calculate_unit_economics — returns nulls rather than guesses
   when CAC is absent, so the UI and the brief can both say "not supplied"
   instead of implying a payback period nobody entered. */
export function calculateUnitEconomics(arpu, churn_rate, cac) {
  const ltv = churn_rate > 0 ? round2(arpu / churn_rate) : null;
  if (!cac || cac <= 0) {
    return { ltv, cac: null, ltv_cac_ratio: null, payback_months: null };
  }
  return {
    ltv,
    cac: round2(cac),
    ltv_cac_ratio: ltv === null ? null : round2(ltv / cac),
    payback_months: arpu > 0 ? round2(cac / arpu) : null,
  };
}

/* Mirrors engine.build_axis. Values come from minimum + k * step rather than
   an accumulator so Python and JS produce bit-identical doubles, with no
   rounding step that would need its own parity rules. */
export function buildAxis(value, minimum, maximum, step, count = 5) {
  const totalSteps = Math.round((maximum - minimum) / step);
  const here = Math.round((value - minimum) / step);
  const span = count - 1;
  const start = totalSteps <= span
    ? 0
    : Math.min(Math.max(here - Math.floor(span / 2), 0), totalSteps - span);

  return Array.from({ length: count }, (_, i) => minimum + (start + i) * step);
}

/* Mirrors engine.calculate_sensitivity_grid. The projection loop multiplies
   MRR by (1 + g - c) every month, so this closed form is the same arithmetic
   the loop performs — not an approximation of it. */
export function calculateSensitivityGrid(current_mrr, churnAxis, growthAxis, months = 12) {
  return churnAxis.map((c) =>
    growthAxis.map((g) => round2(current_mrr * (1 + g - c) ** months * 12))
  );
}

/* Mirrors engine.solve_net_rate — inverts ARR = MRR × (1 + r)^months × 12. */
export function solveNetRate(current_mrr, target_arr, months = 12) {
  if (current_mrr <= 0 || target_arr <= 0) return null;
  return (target_arr / (current_mrr * 12)) ** (1 / months) - 1;
}

/* Mirrors engine.solve_for_target. Either requirement can come back negative —
   a target below today's run rate needs churn above growth — which is an
   answer, not an error, so the caller decides what is in range. */
export function solveForTarget(current_mrr, churn_rate, growth_rate, target_arr, months = 12) {
  const netRate = solveNetRate(current_mrr, target_arr, months);
  if (netRate === null) return { netRate: null, requiredChurn: null, requiredGrowth: null };
  return {
    netRate,
    requiredChurn: growth_rate - netRate,
    requiredGrowth: churn_rate + netRate,
  };
}

/* Mirrors engine.SCENARIOS as (churnMult, growthMult). The parity fixture
   carries the Python copy, and engineParity.test.js asserts these are equal —
   so a scenario cannot be retuned on one side of the app only. */
export const SCENARIOS = {
  base: [1.0, 1.0],
  optimistic: [0.7, 1.4],
  pessimistic: [1.5, 0.6],
};

export function buildModel(inputs) {
  const base = calculateProjections(inputs, ...SCENARIOS.base);
  const optimistic = calculateProjections(inputs, ...SCENARIOS.optimistic);
  const pessimistic = calculateProjections(inputs, ...SCENARIOS.pessimistic);

  let maxSpread = 0;
  let inflectionMonth = 1;
  base.forEach((_, i) => {
    const s = optimistic[i].arr - pessimistic[i].arr;
    if (s > maxSpread) { maxSpread = s; inflectionMonth = base[i].month; }
  });

  const unit = calculateUnitEconomics(inputs.arpu, inputs.churn_rate, inputs.cac);
  const netMonthly = inputs.growth_rate - inputs.churn_rate;

  return {
    base, optimistic, pessimistic, netMonthly, inflectionMonth,
    ltv: unit.ltv,
    unit,
    spread: round2(optimistic.at(-1).arr - pessimistic.at(-1).arr),
    currentArr: round2(inputs.current_mrr * 12),
  };
}

/* ── markdown → memo blocks ─────────────────────────────────────────────────── */
function parseMemo(md) {
  const blocks = [];
  let para = [];
  let list = null;
  const flushPara = () => { if (para.length) { blocks.push({ type: "p", text: para.join(" ") }); para = []; } };
  const flushList = () => { if (list) { blocks.push({ type: "ul", items: list }); list = null; } };
  const flush = () => { flushPara(); flushList(); };

  md.replace(/\r/g, "").split("\n").forEach((raw) => {
    const line = raw.trim();
    if (!line) { flush(); return; }

    const hash = line.match(/^#{1,6}\s+(.*)$/);
    const boldHead = line.match(/^\*\*(.+?)\*\*\s*[:—–-]?\s*(.*)$/);
    const bullet = line.match(/^[-*•]\s+(.*)$/);

    if (hash) {
      flush();
      blocks.push({ type: "h", text: hash[1].replace(/\*\*/g, "").replace(/:$/, "") });
    } else if (boldHead) {
      flush();
      blocks.push({ type: "h", text: boldHead[1].replace(/:$/, "") });
      if (boldHead[2]) para.push(boldHead[2]);
    } else if (bullet) {
      flushPara();
      list = list || [];
      list.push(bullet[1]);
    } else {
      flushList();
      para.push(line);
    }
  });
  flush();

  const firstBody = blocks.findIndex((b) => b.type === "p");
  if (firstBody > -1) blocks[firstBody].pull = true;
  return blocks;
}

function Inline({ text }) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g).filter(Boolean);
  return (
    <>
      {parts.map((p, i) => {
        if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
        if (/^\*[^*]+\*$/.test(p)) return <em key={i}>{p.slice(1, -1)}</em>;
        if (/^`[^`]+`$/.test(p)) return <code key={i}>{p.slice(1, -1)}</code>;
        return <React.Fragment key={i}>{p}</React.Fragment>;
      })}
    </>
  );
}

/* Axis values are built by arithmetic, so 3% arrives as 0.030000000000000002 —
   compare against the slider value with a tolerance well under one step. */
const near = (a, b) => Math.abs(a - b) < 1e-9;

/* Renders one solved lever. A requirement outside the slider's range is the
   interesting answer — the target is not reachable on that lever alone — so it
   is stated with the bound it breaks rather than clamped into looking valid. */
function describeLever(value, slider, label) {
  if (value === null || !isFinite(value)) return <b className="c-dim">—</b>;
  if (value < slider.min) {
    return <b className="c-red">{pct(value)} — below the {pct(slider.min)} {label} floor</b>;
  }
  if (value > slider.max) {
    return <b className="c-red">{pct(value)} — above the {pct(slider.max)} {label} ceiling</b>;
  }
  return <b className="c-teal">{pct(value)}</b>;
}

/* ── static config ──────────────────────────────────────────────────────────── */
const DEFAULTS = { current_mrr: 50000, churn_rate: 0.03, arpu: 150, growth_rate: 0.05, cac: 1500 };

const SLIDERS = [
  { key: "current_mrr", label: "Current MRR", min: 5000, max: 500000, step: 1000, url: "mrr", show: fmt, edge: (v) => fmt(v) },
  { key: "churn_rate", label: "Monthly Churn", min: 0.005, max: 0.15, step: 0.005, url: "churn", show: pct, edge: (v) => pct(v) },
  { key: "arpu", label: "ARPU", min: 20, max: 2000, step: 10, url: "arpu", show: (v) => `$${v}`, edge: (v) => `$${v}` },
  { key: "growth_rate", label: "Monthly Growth", min: 0.01, max: 0.2, step: 0.005, url: "growth", show: pct, edge: (v) => pct(v) },
  { key: "cac", label: "CAC", min: 0, max: 50000, step: 100, url: "cac", show: (v) => (v > 0 ? fmt(v) : "—"), edge: (v) => (v > 0 ? fmt(v) : "none") },
];

/* Labels read the multipliers rather than restating them, so the legend and
   the card notes cannot drift from the numbers actually being applied. */
// Keep one decimal on whole multipliers so base reads "×1.0", not "×1", while
// leaving anything finer untouched rather than rounding it away.
const fmtMult = (v) => (Number.isInteger(v) ? v.toFixed(1) : String(v));

const multText = (key, capitalised = false) => {
  const [c, g] = SCENARIOS[key].map(fmtMult);
  return capitalised
    ? `Churn ×${c} · Growth ×${g}`
    : `churn ×${c} · growth ×${g}`;
};

const SCENARIO_KEY = [
  { label: "BASE", color: "#4f9eff", key: "base" },
  { label: "OPTIMISTIC", color: "#00d4aa", key: "optimistic" },
  { label: "PESSIMISTIC", color: "#ff4d6d", key: "pessimistic" },
];

const FORMULAE = [
  "ARR = MRR × 12",
  "MRRₙ₊₁ = MRRₙ + (MRRₙ×g) − (MRRₙ×c)",
  "LTV = ARPU ÷ churn",
  "LTV/CAC = LTV ÷ CAC",
  "Payback = CAC ÷ ARPU  (months)",
  "Spread = ARR_opt − ARR_pess",
];

const PRESETS = [
  { id: "seed", label: "Seed PLG", inputs: { current_mrr: 25000, churn_rate: 0.045, arpu: 60, growth_rate: 0.09, cac: 400 } },
  { id: "mid", label: "Mid-market", inputs: { current_mrr: 120000, churn_rate: 0.025, arpu: 450, growth_rate: 0.055, cac: 3000 } },
  { id: "ent", label: "Enterprise", inputs: { current_mrr: 400000, churn_rate: 0.01, arpu: 1800, growth_rate: 0.03, cac: 12000 } },
];

/* Shipped with the app so the brief panel is never empty on a cold start.
   Written for the default inputs and labelled as a sample in the UI. */
const SAMPLE_BRIEF = `**Primary Risk Driver**
Churn, not growth, sets the width of this range. At 3.0% monthly the pessimistic path
compounds a 4.5% effective churn against 3.0% new revenue, ending the year at $500.5K ARR —
$260.4K below base. Growth assumptions move the ceiling; churn moves the floor.

**Inflection Point**
The scenarios separate slowly through Q1 and then widen every month. By month 12 the
spread is $564.8K, roughly the size of today's entire run rate, which is the point at which
hiring and pipeline commitments made on the base case stop being reversible.

**Strategic Levers**
- Take 100bps off monthly churn before adding growth spend: it lifts the floor and the ceiling at once.
- Hold growth at 5% and concentrate expansion on the cohorts already above $150 ARPU rather than buying new logos.

**LTV/CAC Implication**
3.0% monthly churn implies a ~33-month customer life and $5.0K LTV at $150 ARPU. Against
$1.5K CAC that is 3.33:1 with payback at month 10 — clear of the 3:1 bar, but thin enough
that a single point of added churn pushes the cohort past its own lifetime.`;

const CACHE_KEY = "sse.brief.v2"; // v2: payload gained cac

function readCache() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p && p.narrative && p.inputs ? p : null;
  } catch (e) { return null; }
}

function writeCache(narrative, inputs) {
  try { window.localStorage.setItem(CACHE_KEY, JSON.stringify({ narrative, inputs })); } catch (e) { /* private mode */ }
}

/* ── URL state ──────────────────────────────────────────────────────────────── */
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

function inputsFromUrl() {
  const out = { ...DEFAULTS };
  if (typeof window === "undefined") return out;
  const q = new URLSearchParams(window.location.search);
  SLIDERS.forEach(({ key, url, min, max }) => {
    const raw = q.get(url);
    if (raw === null || raw === "") return;
    const n = Number(raw);
    if (isFinite(n)) out[key] = clamp(n, min, max);
  });
  return out;
}

/* ── chart tooltip ──────────────────────────────────────────────────────────── */
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="tt">
      <div className="tt-head">MONTH {String(d.month).padStart(2, "0")}</div>
      <div className="tt-row"><span style={{ color: "#00d4aa" }}>Optimistic</span><b>{fmt(d.opt)}</b></div>
      <div className="tt-row"><span style={{ color: "#4f9eff" }}>Base</span><b>{fmt(d.base)}</b></div>
      <div className="tt-row"><span style={{ color: "#ff4d6d" }}>Pessimistic</span><b>{fmt(d.pess)}</b></div>
      <div className="tt-row tt-spread"><span>Spread</span><b>{fmt(d.opt - d.pess)}</b></div>
      <div className="tt-note">{label}</div>
    </div>
  );
};

/* ── main ───────────────────────────────────────────────────────────────────── */
export default function App() {
  const [inputs, setInputs] = useState(inputsFromUrl);
  const [metric, setMetric] = useState("ARR");
  const [narrative, setNarrative] = useState("");
  const [briefInputs, setBriefInputs] = useState(null);
  const [briefSource, setBriefSource] = useState("none"); // none | live | cached | sample
  const [aiState, setAiState] = useState("loading"); // loading | ready | error
  const [elapsed, setElapsed] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [target, setTarget] = useState(null); // null → track 2× current ARR
  const [toast, setToast] = useState("");
  const reqId = useRef(0);

  const model = useMemo(() => buildModel(inputs), [inputs]);

  /* Axis bounds come from the slider config rather than literals so the grid
     cannot drift away from the range the user can actually reach. */
  const sensitivity = useMemo(() => {
    const bounds = (key) => {
      const s = SLIDERS.find((x) => x.key === key);
      return [s.min, s.max, s.step];
    };
    const churnAxis = buildAxis(inputs.churn_rate, ...bounds("churn_rate"));
    const growthAxis = buildAxis(inputs.growth_rate, ...bounds("growth_rate"));
    return {
      churnAxis,
      growthAxis,
      grid: calculateSensitivityGrid(inputs.current_mrr, churnAxis, growthAxis),
    };
  }, [inputs]);

  /* Until the target is edited it tracks the model, so changing MRR doesn't
     leave a stale goal sitting on screen. */
  const effectiveTarget = target ?? Math.round(model.currentArr * 2);
  const solved = solveForTarget(
    inputs.current_mrr, inputs.churn_rate, inputs.growth_rate, effectiveTarget
  );

  const update = (key, val) => setInputs((prev) => ({ ...prev, [key]: val }));

  /* AI brief — enhancement only. Never blocks the numbers. */
  const runBrief = useCallback(async (payload) => {
    const id = ++reqId.current;
    setAiState("loading");
    setElapsed(0);
    try {
      // brief_only: the projections are already on screen — computed locally by
      // the same engine — so ask the API for prose alone instead of 36 rows we
      // would discard. Older deployments ignore the flag and send everything.
      const res = await axios.post(API_URL, { ...payload, brief_only: true }, { timeout: 90000 });
      if (id !== reqId.current) return;
      const text = res.data?.narrative || "";
      // The backend returns its own failures inside `narrative` (200 OK) — treat as error.
      if (!text || /^AI narrative unavailable/i.test(text.trim())) throw new Error("upstream");
      setNarrative(text);
      setBriefInputs(payload);
      setBriefSource("live");
      setAiState("ready");
      writeCache(text, payload);
    } catch (e) {
      if (id !== reqId.current) return;
      // Never leave the panel empty: fall back to the last live brief, then the sample.
      setNarrative((prev) => prev || SAMPLE_BRIEF);
      setBriefInputs((prev) => prev || DEFAULTS);
      setBriefSource((prev) => (prev === "live" || prev === "cached" ? prev : "sample"));
      setAiState("error");
    }
  }, []);

  useEffect(() => {
    const cached = readCache();
    if (cached) {
      setNarrative(cached.narrative);
      setBriefInputs(cached.inputs);
      setBriefSource("cached");
    }
    runBrief(inputsFromUrl());
  }, [runBrief]);

  useEffect(() => {
    if (aiState !== "loading") return undefined;
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [aiState]);

  /* URL sync — debounced so slider drags don't spam history */
  useEffect(() => {
    const t = setTimeout(() => {
      const q = new URLSearchParams();
      SLIDERS.forEach(({ key, url }) => q.set(url, String(inputs[key])));
      window.history.replaceState(null, "", `${window.location.pathname}?${q.toString()}`);
    }, 250);
    return () => clearTimeout(t);
  }, [inputs]);

  useEffect(() => {
    document.body.classList.toggle("sheet-locked", sheetOpen);
    return () => document.body.classList.remove("sheet-locked");
  }, [sheetOpen]);

  const flash = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2000);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      flash("LINK COPIED · MODEL STATE ENCODED IN URL");
    } catch (e) {
      flash("COPY BLOCKED — SELECT THE ADDRESS BAR");
    }
  };

  const exportCsv = () => {
    const head = ["month", "base_mrr", "base_arr", "optimistic_mrr", "optimistic_arr", "pessimistic_mrr", "pessimistic_arr", "arr_spread"];
    const rows = model.base.map((b, i) => {
      const o = model.optimistic[i];
      const p = model.pessimistic[i];
      return [b.month, csvNum(b.mrr), csvNum(b.arr), csvNum(o.mrr), csvNum(o.arr), csvNum(p.mrr), csvNum(p.arr), csvNum(o.arr - p.arr)].join(",");
    });
    const meta = [
      `# saas scenario engine`,
      `# current_mrr,${inputs.current_mrr}`,
      `# churn_rate,${inputs.churn_rate}`,
      `# arpu,${inputs.arpu}`,
      `# growth_rate,${inputs.growth_rate}`,
      `# cac,${inputs.cac || ""}`,
      `# ltv,${model.unit.ltv ?? ""}`,
      `# ltv_cac_ratio,${model.unit.ltv_cac_ratio ?? ""}`,
      `# payback_months,${model.unit.payback_months ?? ""}`,
    ];
    const blob = new Blob([[...meta, head.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `scenario_mrr${inputs.current_mrr}_churn${(inputs.churn_rate * 100).toFixed(1)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    flash("CSV EXPORTED · 12 MONTHS × 3 SCENARIOS");
  };

  /* chart series — stacked areas draw the confidence band */
  const chartData = model.base.map((b, i) => {
    const k = metric === "ARR" ? "arr" : "mrr";
    const o = model.optimistic[i][k];
    const p = model.pessimistic[i][k];
    return { month: b.month, label: `M${b.month}`, base: b[k], opt: o, pess: p, floor: p, band: o - p };
  });

  const last = chartData.at(-1);
  const spreadRatio = model.currentArr > 0 ? model.spread / model.currentArr : 0;
  const stale = briefInputs && SLIDERS.some(({ key }) => briefInputs[key] !== inputs[key]);

  const cards = [
    { label: "Base ARR · M12", value: fmt(model.base.at(-1).arr), color: "#4f9eff", note: `${model.netMonthly >= 0 ? "+" : ""}${(model.netMonthly * 100).toFixed(1)}% net MRR / mo` },
    { label: "Optimistic ARR", value: fmt(model.optimistic.at(-1).arr), color: "#00d4aa", note: `▲ ${multText("optimistic")}` },
    { label: "Pessimistic ARR", value: fmt(model.pessimistic.at(-1).arr), color: "#ff4d6d", note: `▼ ${multText("pessimistic")}` },
    model.unit.ltv_cac_ratio !== null
      ? {
          label: "LTV / CAC",
          value: `${model.unit.ltv_cac_ratio.toFixed(2)}:1`,
          color: model.unit.ltv_cac_ratio >= 3 ? "#00d4aa" : "#ffd666",
          note: `LTV ${fmt(model.unit.ltv)} · payback ${model.unit.payback_months} mo`,
        }
      : { label: "Customer LTV", value: model.ltv ? fmt(model.ltv) : "—", color: "#ffd666", note: `ARPU ÷ ${pct(inputs.churn_rate)} churn · no CAC set` },
  ];

  const memoBlocks = narrative ? parseMemo(narrative) : [];
  const briefFor = briefInputs &&
    `MRR ${fmt(briefInputs.current_mrr)} · churn ${pct(briefInputs.churn_rate)} · ARPU $${briefInputs.arpu} · growth ${pct(briefInputs.growth_rate)}${briefInputs.cac ? ` · CAC ${fmt(briefInputs.cac)}` : ""}`;
  const provenance = briefInputs && {
    live: `Generated live for ${briefFor}`,
    cached: `Cached from your last live run · ${briefFor}`,
    sample: "Sample brief shipped with the app — the narrative service is offline. Every figure above was computed live in your browser.",
    none: "",
  }[briefSource];

  const controls = (
    <>
      <div className="sheet-grip" />
      <div className="sheet-head">
        <span className="section-label">Input parameters</span>
        <button className="ghost-btn sheet-close" onClick={() => setSheetOpen(false)}>Done</button>
      </div>

      <div className="presets">
        {PRESETS.map((p) => {
          const active = SLIDERS.every(({ key }) => p.inputs[key] === inputs[key]);
          return (
            <button key={p.id} className={`preset-btn ${active ? "active" : ""}`}
              onClick={() => setInputs(p.inputs)}>{p.label}</button>
          );
        })}
      </div>

      <div className="sliders">
        {SLIDERS.map(({ key, label, min, max, step, show, edge }) => {
          const v = inputs[key];
          const filled = ((v - min) / (max - min)) * 100;
          return (
            <div className="slider" key={key}>
              <div className="slider-top">
                <label htmlFor={key}>{label}</label>
                <output htmlFor={key}>{show(v)}</output>
              </div>
              <input
                id={key} type="range" min={min} max={max} step={step} value={v}
                aria-label={label}
                onChange={(e) => update(key, +e.target.value)}
                style={{ background: `linear-gradient(90deg, var(--blue) 0%, var(--blue) ${filled}%, var(--line) ${filled}%, var(--line) 100%)` }}
              />
              <div className="slider-edges"><span>{edge(min)}</span><span>{edge(max)}</span></div>
            </div>
          );
        })}
      </div>

      <button className="run-btn" onClick={() => runBrief(inputs)} disabled={aiState === "loading"}>
        {aiState === "loading" ? "Writing brief…" : "Regenerate AI brief"}
      </button>
      <p className="run-note">
        Projections update live — the model runs in your browser. This button only rewrites the AI narrative.
      </p>

      <div className="side-block">
        <span className="section-label">Math formulae</span>
        {FORMULAE.map((f) => <div className="formula" key={f}>{f}</div>)}
      </div>

      <div className="side-block">
        <span className="section-label">Scenario multipliers</span>
        {SCENARIO_KEY.map(({ label, color, key }) => (
          <div className="scenario-row" key={label}>
            <span className="scenario-bar" style={{ background: color }} />
            <span className="scenario-name" style={{ color }}>{label}</span>
            <span className="scenario-desc">{multText(key, true)}</span>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark" />
          <span className="brand-title">SAAS SCENARIO ENGINE</span>
          <span className="brand-sub">FP&amp;A modeling · deterministic math + LLM narrative</span>
        </div>
        <div className="topbar-right">
          <span className={`engine-state ${aiState}`}>
            <i /> {aiState === "loading" ? "AI BRIEF · WARMING" : aiState === "error" ? "AI BRIEF · OFFLINE" : "AI BRIEF · READY"}
          </span>
          <button className="ghost-btn" onClick={copyLink}>Copy link</button>
          <button className="ghost-btn" onClick={exportCsv}>Export CSV</button>
        </div>
      </header>

      <div className="layout">
        <aside className={`sidebar ${sheetOpen ? "open" : ""}`}>{controls}</aside>
        {sheetOpen && <div className="sheet-backdrop" onClick={() => setSheetOpen(false)} />}

        <main className="main">
          <section className="intro">
            <p>
              Move any input and 12 months of base, optimistic and pessimistic revenue recompute in
              the browser using the same formulas the FastAPI service runs. Python owns the
              arithmetic; the model only writes the brief.
            </p>
            <div className="intro-meta">
              <span>Python · FastAPI — math</span>
              <span>Groq · Llama 3.3 — prose</span>
              <span>React · Recharts — UI</span>
              <a href="https://github.com/guramolsingh/saas-scenario-engine" target="_blank" rel="noreferrer">Source ↗</a>
            </div>
          </section>

          <section className="hero">
            <div className="hero-lead">
              <div className="hero-label">Scenario spread · month 12 ARR</div>
              <div className="hero-figure">{fmt(model.spread)}</div>
              <p className="hero-read">
                Optimistic minus pessimistic ARR — the dollar width of the uncertainty you are
                underwriting. That is <strong>{spreadRatio.toFixed(1)}×</strong> today&apos;s {fmt(model.currentArr)} run rate,
                and the paths diverge fastest into month {model.inflectionMonth}.
              </p>
              <div className="hero-band">
                <span className="hero-band-pess">{fmt(model.pessimistic.at(-1).arr)}</span>
                <span className="hero-band-track"><i /></span>
                <span className="hero-band-opt">{fmt(model.optimistic.at(-1).arr)}</span>
              </div>
            </div>
            <div className="cards">
              {cards.map(({ label, value, color, note }) => (
                <div className="card" key={label} style={{ "--accent": color }}>
                  <div className="card-label">{label}</div>
                  <div className="card-value" style={{ color }}>{value}</div>
                  <div className="card-note">{note}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="panel">
            <div className="panel-header">
              <div>
                <h2 className="panel-title">12-month revenue trajectory</h2>
                <div className="panel-sub">Shaded band = optimistic-to-pessimistic range · solid line = base case</div>
              </div>
              <div className="panel-tools">
                <div className="tab-group" role="tablist">
                  {["ARR", "MRR"].map((t) => (
                    <button key={t} role="tab" aria-selected={metric === t}
                      className={`tab-btn ${metric === t ? "active" : ""}`}
                      onClick={() => setMetric(t)}>{t}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="chart-wrap">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 4, bottom: 0 }}>
                  <defs>
                    <linearGradient id="bandFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00d4aa" stopOpacity={0.16} />
                      <stop offset="100%" stopColor="#ff4d6d" stopOpacity={0.14} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="2 5" stroke="#18213a" vertical={false} />
                  <XAxis dataKey="label" tickMargin={10}
                    tick={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 12, fill: "#8298bb" }}
                    axisLine={{ stroke: "#18213a" }} tickLine={false} />
                  <YAxis tickFormatter={fmt} width={68} tickMargin={8} domain={["auto", "auto"]}
                    tick={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 12, fill: "#8298bb" }}
                    axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#2b3a5c", strokeWidth: 1 }} />
                  <Area type="monotone" dataKey="floor" stackId="band" stroke="none" fill="none" isAnimationActive={false} />
                  <Area type="monotone" dataKey="band" stackId="band" stroke="none" fill="url(#bandFill)" isAnimationActive={false} />
                  <Line type="monotone" dataKey="opt" stroke="#00d4aa" strokeWidth={1.25} strokeDasharray="5 4" dot={false} isAnimationActive={false} />
                  <Line type="monotone" dataKey="pess" stroke="#ff4d6d" strokeWidth={1.25} strokeDasharray="5 4" dot={false} isAnimationActive={false} />
                  <Line type="monotone" dataKey="base" stroke="#4f9eff" strokeWidth={2.75} dot={false} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="chart-foot">
              <span><i className="sw" style={{ background: "#4f9eff" }} />Base {fmt(last.base)}</span>
              <span><i className="sw dash" style={{ background: "#00d4aa" }} />Optimistic {fmt(last.opt)}</span>
              <span><i className="sw dash" style={{ background: "#ff4d6d" }} />Pessimistic {fmt(last.pess)}</span>
              <span className="foot-right">Band width at M12 · {fmt(last.opt - last.pess)}</span>
            </div>
          </section>

          <section className="panel">
            <div className="panel-header">
              <div>
                <h2 className="panel-title">Scenario table · {metric}</h2>
                <div className="panel-sub">12 months × 3 scenarios, computed client-side</div>
              </div>
              <button className="ghost-btn" onClick={exportCsv}>Export CSV</button>
            </div>

            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Month</th><th>Base</th><th>Optimistic</th><th>Pessimistic</th><th>Spread</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.map((r) => {
                    const sp = r.opt - r.pess;
                    const w = (sp / (last.opt - last.pess)) * 100;
                    return (
                      <tr key={r.month}>
                        <th scope="row">M{String(r.month).padStart(2, "0")}</th>
                        <td className="c-blue">{fmt(r.base)}</td>
                        <td className="c-teal">{fmt(r.opt)}</td>
                        <td className="c-red">{fmt(r.pess)}</td>
                        <td className="c-amber spread-cell">
                          <span className="spread-bar" style={{ width: `${w}%` }} />
                          <span className="spread-val">{fmt(sp)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <ul className="row-cards">
              {chartData.map((r) => (
                <li key={r.month}>
                  <div className="rc-head">
                    <span className="rc-month">M{String(r.month).padStart(2, "0")}</span>
                    <span className="rc-spread">Spread {fmt(r.opt - r.pess)}</span>
                  </div>
                  <div className="rc-grid">
                    <span>Base</span><b className="c-blue">{fmt(r.base)}</b>
                    <span>Opt</span><b className="c-teal">{fmt(r.opt)}</b>
                    <span>Pess</span><b className="c-red">{fmt(r.pess)}</b>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="panel">
            <div className="panel-header">
              <div>
                <h2 className="panel-title">Sensitivity &amp; inverse solve</h2>
                <div className="panel-sub">
                  Month-12 base ARR across churn × growth · and what each lever must reach for a target
                </div>
              </div>
            </div>

            <div className="sens-body">
              <div className="sens-block">
                <div className="sens-label">Month 12 base ARR · churn (rows) × growth (columns)</div>
                <div className="table-scroll">
                  <table className="data-table sens-grid">
                    <thead>
                      <tr>
                        <th>Churn \ Growth</th>
                        {sensitivity.growthAxis.map((g) => (
                          <th key={g} className={near(g, inputs.growth_rate) ? "sens-axis-on" : ""}>{pct(g)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sensitivity.churnAxis.map((c, ri) => (
                        <tr key={c}>
                          <th scope="row" className={near(c, inputs.churn_rate) ? "sens-axis-on" : ""}>{pct(c)}</th>
                          {sensitivity.grid[ri].map((v, ci) => {
                            const here = near(c, inputs.churn_rate) && near(sensitivity.growthAxis[ci], inputs.growth_rate);
                            const delta = model.base.at(-1).arr > 0 ? v / model.base.at(-1).arr - 1 : 0;
                            // Tint against today's base case, not the grid's own
                            // range: the question is better or worse than now.
                            const a = Math.min(Math.abs(delta) / 0.5, 1) * 0.3;
                            const tint = delta >= 0 ? `rgba(0,212,170,${a})` : `rgba(255,77,109,${a})`;
                            return (
                              <td key={ci} className={`sens-cell ${here ? "sens-here" : ""}`}
                                style={{ background: tint }}
                                title={`${pct(c)} churn · ${pct(sensitivity.growthAxis[ci])} growth → ${fmt(v)}`}>
                                {fmt(v)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="sens-note">
                  Outlined cell is today&apos;s model. Green is above the current base case, red below —
                  each step is one slider notch, so the grid shows what a single click of either lever is worth.
                </p>
              </div>

              <div className="sens-block">
                <div className="sens-label">What gets me to a target?</div>
                <div className="solver-row">
                  <label htmlFor="target-arr">Target month-12 ARR</label>
                  <input
                    id="target-arr" type="number" min="0" step="50000"
                    className="solver-input"
                    value={effectiveTarget}
                    onChange={(e) => setTarget(e.target.value === "" ? null : Math.max(0, +e.target.value))}
                  />
                </div>

                <ul className="solver-out">
                  <li>
                    <span>Required churn</span>
                    <em>holding growth at {pct(inputs.growth_rate)}</em>
                    {describeLever(solved.requiredChurn, SLIDERS.find((s) => s.key === "churn_rate"), "churn")}
                  </li>
                  <li>
                    <span>Required growth</span>
                    <em>holding churn at {pct(inputs.churn_rate)}</em>
                    {describeLever(solved.requiredGrowth, SLIDERS.find((s) => s.key === "growth_rate"), "growth")}
                  </li>
                  <li>
                    <span>Net monthly rate</span>
                    <em>growth − churn, compounded 12×</em>
                    <b className="c-amber">{solved.netRate === null ? "—" : pct(solved.netRate)}</b>
                  </li>
                </ul>
                <p className="sens-note">
                  Inverts ARR = MRR × (1 + r)¹² × 12 — the same compounding the projection runs, solved
                  for r rather than searched.
                </p>
              </div>
            </div>
          </section>

          <section className="panel memo">
            <div className="panel-header">
              <div>
                <h2 className="panel-title">AI CFO executive brief</h2>
                <div className="panel-sub">Llama 3.3 70B reads the computed results · every number above is deterministic</div>
              </div>
              <div className="panel-tools">
                {aiState === "loading" && narrative && <span className="chip">Refreshing…</span>}
                {aiState !== "loading" && briefSource === "cached" && <span className="chip">Cached</span>}
                {aiState !== "loading" && briefSource === "sample" && <span className="chip warn">Sample</span>}
                {aiState !== "loading" && stale && (
                  <button className="ghost-btn warn" onClick={() => runBrief(inputs)}>Regenerate</button>
                )}
                {aiState === "error" && !stale && (
                  <button className="ghost-btn" onClick={() => runBrief(inputs)}>Retry</button>
                )}
              </div>
            </div>

            {!narrative && aiState === "loading" && (
              <div className="memo-body">
                <div className="warming">
                  <span className="pulse" />
                  Warming up the analysis engine — the narrative service is on a free tier and cold-starts in up to 60s.
                  Charts and tables above are already final. <b>{String(elapsed).padStart(2, "0")}s</b>
                </div>
                <div className="skeleton">
                  {[92, 78, 96, 64, 88, 71].map((w, i) => <span key={i} style={{ width: `${w}%` }} />)}
                </div>
              </div>
            )}

            {narrative && (
              <div className="memo-body">
                {memoBlocks.map((b, i) => {
                  if (b.type === "h") return <h3 className="memo-h" key={i}><Inline text={b.text} /></h3>;
                  if (b.type === "ul") return (
                    <ul className="memo-ul" key={i}>
                      {b.items.map((it, j) => <li key={j}><Inline text={it} /></li>)}
                    </ul>
                  );
                  return b.pull
                    ? <blockquote className="memo-pull" key={i}><Inline text={b.text} /></blockquote>
                    : <p className="memo-p" key={i}><Inline text={b.text} /></p>;
                })}
                <div className="memo-foot">{provenance}</div>
              </div>
            )}
          </section>

          <footer className="page-foot">
            Python + FastAPI for math · Groq/Llama for prose · client-side engine mirrors <code>engine.py</code> so the page is never empty.
          </footer>
        </main>
      </div>

      <div className="mobile-bar">
        <div className="mb-read">
          <span className="mb-label">Spread M12</span>
          <span className="mb-value">{fmt(model.spread)}</span>
        </div>
        <button className="mb-btn" onClick={() => setSheetOpen(true)}>Adjust inputs</button>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
